import { useState, useCallback } from 'react';
import {
    Box,
    useToast,
    Alert,
    AlertIcon,
    Progress,
    Text,
} from '@chakra-ui/react';
import { axios } from '@/services/axios';

import { Button } from '@/components/v4';
import AddInvoiceLineTable from './table/AddInvoiceLineTable';
import { initTableState, isRowComplete, calculateLineTotal } from './constants';

const InvoiceLinesAdd = ({
    invoice,
    existingLines = [],
    onBack,
    onSuccess,
}) => {
    const toast = useToast();

    const [aggregateTable, setAggregateTable] = useState(initTableState);
    const [canSubmit, setCanSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitProgress, setSubmitProgress] = useState({
        current: 0,
        total: 0,
    });
    const [errors, setErrors] = useState([]);

    const updateAggregateTable = useCallback((updater) => {
        setAggregateTable((prev) => {
            if (typeof updater === 'function') {
                return updater(prev);
            }
            return updater;
        });
    }, []);

    const handleSubmit = async () => {
        // Filter only complete rows
        const rowsToSubmit = aggregateTable.filter(isRowComplete);

        if (rowsToSubmit.length === 0) {
            toast({
                title: 'No valid rows',
                description:
                    'Please fill in all required fields for at least one row.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsSubmitting(true);
        setSubmitProgress({ current: 0, total: rowsToSubmit.length });
        setErrors([]);

        const results = [];
        const submitErrors = [];

        // Submit each row sequentially
        for (let i = 0; i < rowsToSubmit.length; i++) {
            const row = rowsToSubmit[i];
            setSubmitProgress({ current: i + 1, total: rowsToSubmit.length });

            try {
                const payload = {
                    invoiceID: invoice?.id,
                    itemCode: row.itemCode,
                    quantity: parseInt(row.quantity, 10),
                    unitPrice: parseFloat(row.unitPrice),
                    description: row.description,
                    lineTotal: calculateLineTotal(row.quantity, row.unitPrice),
                };

                const res = await axios.post('/invoiceline/', payload);

                if (res?.data?.error) {
                    submitErrors.push({
                        row: i + 1,
                        description: row.description || row.itemCode,
                        error: res.data.message || res.data.error,
                    });
                } else {
                    results.push(res.data);
                }
            } catch (error) {
                submitErrors.push({
                    row: i + 1,
                    description: row.description || row.itemCode,
                    error:
                        error?.response?.data?.message ||
                        error.message ||
                        'Unknown error',
                });
            }
        }

        setIsSubmitting(false);

        if (submitErrors.length > 0) {
            setErrors(submitErrors);
            toast({
                title: 'Some rows failed',
                description: `${results.length} of ${rowsToSubmit.length} invoice lines created.`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Success',
                description: `${results.length} invoice line${results.length > 1 ? 's' : ''} created successfully.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onSuccess?.();
        }
    };

    return (
        <Box>
            {/* Error display */}
            {errors.length > 0 && (
                <Alert
                    status="error"
                    my={4}
                    maxW="895px"
                    flexDirection="column"
                    alignItems="flex-start">
                    <Box display="flex" alignItems="center" mb={2}>
                        <AlertIcon />
                        <Text fontWeight="bold">Some rows failed to save:</Text>
                    </Box>
                    {errors.map((err, idx) => (
                        <Text key={idx} fontSize="sm" ml={8}>
                            Row {err.row} ({err.description}): {err.error}
                        </Text>
                    ))}
                </Alert>
            )}

            {/* Progress bar during submission */}
            {isSubmitting && (
                <Box my={4} maxW="895px">
                    <Text fontSize="sm" mb={2}>
                        Submitting {submitProgress.current} of{' '}
                        {submitProgress.total}...
                    </Text>
                    <Progress
                        value={
                            (submitProgress.current / submitProgress.total) *
                            100
                        }
                        size="sm"
                        colorScheme="blue"
                        borderRadius="md"
                    />
                </Box>
            )}

            <AddInvoiceLineTable
                aggregateTable={aggregateTable}
                updateAggregateTable={updateAggregateTable}
                setCanSubmit={setCanSubmit}
                existingLines={existingLines}
            />

            {/* Action buttons - left aligned with Save first */}
            <Box
                display="flex"
                justifyContent="flex-start"
                gap={3}
                mt={6}
                maxW="895px">
                <Button
                    type="submit"
                    className="h-10 min-w-[5rem] font-semibold disabled:cursor-not-allowed"
                    sx={{ width: 142 }}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    isDisabled={!canSubmit || isSubmitting}>
                    Save
                </Button>
                <Button
                    className="secondary h-10 min-w-[5rem] font-semibold"
                    variant="outline"
                    background="white"
                    sx={{ width: 142 }}
                    onClick={onBack}
                    isDisabled={isSubmitting}>
                    Back
                </Button>
            </Box>
        </Box>
    );
};

export default InvoiceLinesAdd;
